import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, Pin, Star, Trash2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PostManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchPosts();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/posts/review-queue?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (postId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/api/admin/posts/${postId}/review`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPosts();
      alert(`Post ${status} successfully!`);
    } catch (error) {
      console.error('Error reviewing post:', error);
      alert('Failed to review post');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedPosts.length === 0) {
      alert('Please select posts first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/posts/bulk-action`,
        { post_ids: selectedPosts, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedPosts([]);
      fetchPosts();
      alert(`Bulk ${action} completed!`);
    } catch (error) {
      console.error('Error in bulk action:', error);
      alert('Bulk action failed');
    }
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading posts...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters & Bulk Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
              >
                Pending
              </Button>
              <Button
                variant={filter === 'approved' ? 'default' : 'outline'}
                onClick={() => setFilter('approved')}
              >
                Approved
              </Button>
              <Button
                variant={filter === 'flagged' ? 'default' : 'outline'}
                onClick={() => setFilter('flagged')}
              >
                Flagged
              </Button>
            </div>

            {selectedPosts.length > 0 && (
              <div className="flex space-x-2">
                <span className="text-sm text-gray-600 self-center">
                  {selectedPosts.length} selected
                </span>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleBulkAction('approve')}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => handleBulkAction('reject')}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('pin')}
                >
                  <Pin className="w-4 h-4 mr-1" />
                  Pin
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No {filter} posts</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <Checkbox
                    checked={selectedPosts.includes(post.id)}
                    onCheckedChange={() => togglePostSelection(post.id)}
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <img
                          src={post.author?.avatar || '/default-avatar.png'}
                          alt={post.author?.name}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <p className="font-semibold">{post.author?.name || 'Unknown User'}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge className={`
                        ${post.review_status === 'pending' ? 'bg-orange-100 text-orange-800' : ''}
                        ${post.review_status === 'approved' ? 'bg-green-100 text-green-800' : ''}
                        ${post.review_status === 'flagged' ? 'bg-red-100 text-red-800' : ''}
                      `}>
                        {post.review_status}
                      </Badge>
                    </div>

                    <p className="text-gray-800 mb-4">{post.content}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex space-x-4 text-sm text-gray-500">
                        <span>{post.likes || 0} likes</span>
                        <span>{post.comments || 0} comments</span>
                        <span>{post.views || 0} views</span>
                      </div>

                      <div className="flex space-x-2">
                        {filter === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleReview(post.id, 'approved')}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => handleReview(post.id, 'rejected')}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PostManagement;